import type {
  QueryFunctionContext,
  UseInfiniteQueryOptions
} from '@tanstack/react-query';
import { axiosHelper } from '@setvi/shared/services';
import { AxiosMethods, QuotesV2Api } from '@setvi/shared/enums';

import type {
  GetQuotesV2Body,
  GetQuotesV2Response,
  QuoteShareSummary,
  QuoteSharesListParams,
  QuoteSharesListResponse,
  QuoteV2Status,
  QuoteV2SummaryStatsResponse
} from './Types';

export enum QuotesV2QueryKey {
  GET_CONFIGURATION = 'GET_QUOTE_V2_CONFIGURATION',
  GET_STATUSES = 'GET_QUOTE_V2_STATUSES',
  GET_QUOTE = 'GET_QUOTE_V2',
  GET_ITEMS_METADATA = 'GET_QUOTE_V2_ITEMS_METADATA',
  GET_QUOTES = 'GET_QUOTES_V2',
  GET_SHARED_QUOTES = 'GET_SHARED_QUOTES_V2',
  GET_SUMMARY_STATS = 'GET_QUOTES_V2_SUMMARY_STATS',
  GET_SHARED_SUMMARY_STATS = 'GET_SHARED_QUOTES_V2_SUMMARY_STATS',
  PREVIEW_PDF = 'GET_QUOTE_V2_PREVIEW_PDF',
  GET_SHARES = 'GET_QUOTE_V2_SHARES',
  GET_QUOTE_SHARES = 'GET_QUOTE_V2_QUOTE_SHARES',
  GET_QUOTE_SHARE_DETAIL = 'GET_QUOTE_V2_QUOTE_SHARE_DETAIL'
}

export const getQuoteV2ConfigurationQuery = () => ({
  queryKey: [QuotesV2QueryKey.GET_CONFIGURATION],
  queryFn: () =>
    axiosHelper({
      endpoint: QuotesV2Api.Configuration,
      method: AxiosMethods.GET,
      baseURL: process.env.REVOPS_API
    })
});

export const getQuoteV2StatusesQuery = () => ({
  queryKey: [QuotesV2QueryKey.GET_STATUSES],
  queryFn: () =>
    axiosHelper({
      endpoint: QuotesV2Api.Statuses,
      method: AxiosMethods.GET,
      baseURL: process.env.REVOPS_API
    }),
  select: (res: QuoteV2Status[]) => res.sort((a, b) => a.order - b.order)
});

const isQuoteAccessDenied = (error: any) => {
  const status = error?.response?.status;
  return status === 401 || status === 403;
};

export const getQuoteV2Query = (quoteId: string) => ({
  queryKey: [QuotesV2QueryKey.GET_QUOTE, quoteId],
  queryFn: () =>
    axiosHelper({
      endpoint: QuotesV2Api.Get.replace(':quoteId', quoteId),
      method: AxiosMethods.GET,
      baseURL: process.env.REVOPS_API
    }),
  // Don't retry when the user lacks access — the result won't change and the
  // details page renders a dedicated "no access" state instead of hammering it.
  retry: (failureCount: number, error: any) => {
    if (isQuoteAccessDenied(error)) return false;
    return failureCount < 3;
  },
  // Once it's a 401/403, don't auto-refetch (focus/reconnect/remount) either —
  // access won't change without a reload, so stop hitting the endpoint.
  refetchOnWindowFocus: (query: any) => !isQuoteAccessDenied(query.state.error),
  refetchOnReconnect: (query: any) => !isQuoteAccessDenied(query.state.error),
  refetchOnMount: (query: any) => !isQuoteAccessDenied(query.state.error)
});

export const getQuoteV2ItemsMetadataQuery = (quoteId: string) => ({
  queryKey: [QuotesV2QueryKey.GET_ITEMS_METADATA, quoteId],
  queryFn: () =>
    axiosHelper({
      endpoint: QuotesV2Api.ItemsMetadata.replace(':quoteId', quoteId),
      method: AxiosMethods.GET,
      baseURL: process.env.REVOPS_API
    })
});

export const getQuotesV2Query = (body: GetQuotesV2Body) => ({
  queryKey: [QuotesV2QueryKey.GET_QUOTES, body],
  queryFn: () =>
    axiosHelper({
      endpoint: QuotesV2Api.GetQuotes,
      method: AxiosMethods.POST,
      baseURL: process.env.REVOPS_API,
      body
    }) as Promise<GetQuotesV2Response>
});

export const getSharedQuotesV2Query = (body: GetQuotesV2Body) => ({
  queryKey: [QuotesV2QueryKey.GET_SHARED_QUOTES, body],
  queryFn: () =>
    axiosHelper({
      endpoint: QuotesV2Api.GetSharedQuotes,
      method: AxiosMethods.POST,
      baseURL: process.env.REVOPS_API,
      body
    }) as Promise<GetQuotesV2Response>
});

export const getQuoteV2PreviewQuery = (
  quoteId: string,
  showManufacturerDetails?: boolean
) => ({
  queryKey: [
    QuotesV2QueryKey.PREVIEW_PDF,
    { quoteId, showManufacturerDetails }
  ],
  queryFn: () =>
    axiosHelper({
      endpoint: QuotesV2Api.PreviewPDF.replace(':quoteId', quoteId),
      params: { showManufacturerDetails },
      method: AxiosMethods.GET,
      baseURL: process.env.REVOPS_API
    })
});

export const getQuotesV2SummaryStatsQuery = (body: GetQuotesV2Body) => ({
  queryKey: [QuotesV2QueryKey.GET_SUMMARY_STATS, body],
  queryFn: () =>
    axiosHelper({
      endpoint: QuotesV2Api.SummaryStats,
      method: AxiosMethods.POST,
      baseURL: process.env.REVOPS_API,
      body
    }) as Promise<QuoteV2SummaryStatsResponse>
});

export const getSharedQuotesV2SummaryStatsQuery = (body: GetQuotesV2Body) => ({
  queryKey: [QuotesV2QueryKey.GET_SHARED_SUMMARY_STATS, body],
  queryFn: () =>
    axiosHelper({
      endpoint: QuotesV2Api.SharedSummaryStats,
      method: AxiosMethods.POST,
      baseURL: process.env.REVOPS_API,
      body
    }) as Promise<QuoteV2SummaryStatsResponse>
});

export const getQuoteV2SharesQuery = (quoteId: string) => ({
  queryKey: [QuotesV2QueryKey.GET_SHARES, quoteId],
  queryFn: () =>
    axiosHelper({
      endpoint: QuotesV2Api.Shares.replace(':quoteId', quoteId),
      method: AxiosMethods.GET,
      baseURL: process.env.REVOPS_API
    }) as Promise<QuoteShareSummary[]>
});

export const getQuoteSharesListQuery = (
  params: QuoteSharesListParams
): UseInfiniteQueryOptions<QuoteSharesListResponse> => ({
  queryKey: [QuotesV2QueryKey.GET_QUOTE_SHARES, params],
  queryFn: (ctx: QueryFunctionContext) => {
    const offset =
      typeof ctx.pageParam === 'number' ? ctx.pageParam : params.offset;
    return axiosHelper({
      endpoint: QuotesV2Api.QuoteShares,
      method: AxiosMethods.GET,
      baseURL: process.env.REVOPS_API,
      params: {
        limit: params.limit,
        offset,
        ...(params.searchStr ? { searchStr: params.searchStr } : {})
      }
    }) as Promise<QuoteSharesListResponse>;
  },
  getNextPageParam: (lastPage, allPages) => {
    const loaded = allPages.reduce(
      (acc, page) => acc + (page?.data?.length || 0),
      0
    );
    if (loaded >= (lastPage?.total || 0)) return undefined;
    return loaded;
  },
  refetchOnWindowFocus: false
});

export const getQuoteShareDetailQuery = (shareId: string) => ({
  queryKey: [QuotesV2QueryKey.GET_QUOTE_SHARE_DETAIL, shareId],
  queryFn: () =>
    axiosHelper({
      endpoint: QuotesV2Api.QuoteShareDetail.replace(':shareId', shareId),
      method: AxiosMethods.GET,
      baseURL: process.env.REVOPS_API
    }) as Promise<QuoteShareSummary>
});
